"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheFynnPhilosophy() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="99397" id="built-on-salesforce-trust" className="dark bg-background text-foreground py-[120px] px-6 md:px-12 lg:px-24 overflow-hidden relative">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center relative z-10">
        
        {}
        <div ref={imageRef} className={`lg:col-span-5 relative transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="relative rounded-3xl overflow-hidden aspect-[3/4] md:aspect-[4/5] bg-card border border-border/50 shadow-2xl">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1608524/img/salesforce_trust_infrastructure.png" alt="Salesforce Trust Infrastructure" className="w-full h-full object-cover brightness-100" />
            
            {}
            <div className="absolute -bottom-2 -right-2 md:-bottom-6 md:-right-6 w-[85%] h-[40%] bg-white/10 backdrop-blur-2xl border border-white/20 rounded-tl-3xl p-6 md:p-8 flex flex-col justify-end shadow-2xl">
              <div className="w-12 h-[2px] bg-primary mb-4 opacity-100"></div>
              <p className="text-xs md:text-sm text-foreground font-mono uppercase tracking-[0.2em] leading-relaxed">
                Enterprise Shield <br />
                <span className="text-primary font-bold">Verified Compliance</span>
              </p>
            </div>
          </div>
        </div>

        {}
        <div ref={textRef} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 delay-200 ease-out ${isTextVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
          <div className="flex items-center gap-4 mb-8 md:mb-12">
            <div className="h-[2px] w-8 bg-primary"></div>
            <span className="text-primary font-sans uppercase tracking-[0.3em] text-xs md:text-sm font-bold">
              Why Salesforce?
            </span>
          </div>

          <blockquote className="font-sans font-bold text-3xl md:text-5xl lg:text-[3.5rem] leading-[1.1] text-foreground tracking-tighter mb-12 relative">
            <span className="absolute -top-10 -left-6 text-8xl text-primary/10 font-serif select-none">"</span>
            The World's Most Trusted Cloud for the World's Most Regulated Industry.
          </blockquote>

          <div className="flex flex-col border-l-2 border-primary/30 pl-8">
            {}
            <span className="font-sans font-semibold text-2xl md:text-3xl text-foreground tracking-wide mb-3">
              Security • Scalability • Compliance
            </span>
            <span className="text-muted-foreground text-xs md:text-sm uppercase tracking-[0.25em] font-mono font-medium">
              Global Standards & Infrastructure
            </span>
          </div>
        </div>

      </div>
    </section>;
}